import { createTRPCClient, httpBatchLink, httpLink, type TRPCClient } from "@trpc/client";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@effekt/api";
import type { MainLocale } from "../../context/MainLocaleContext";
import { restLink, restRoutes } from "./restLink";

export type { AppRouter };
export type TrpcClient = TRPCClient<AppRouter>;
/** What each procedure returns (after its output schema), e.g. RouterOutputs["minside"]["donations"] */
export type RouterOutputs = inferRouterOutputs<AppRouter>;

/**
 * Which backend the site talks to. The Danish site's backend (giveffektivt/donation-platform) has
 * no tRPC endpoint, so it gets REST through restLink; every other site uses our backend's /trpc.
 * Derived from the Sanity site setting main_locale, the same switch Min Side already uses for
 * Danish behaviour, so no deployment setting is needed.
 */
export type ApiFlavor = "trpc" | "rest";
export const apiFlavorFor = (mainLocale: MainLocale): ApiFlavor =>
  mainLocale === "dk" ? "rest" : "trpc";

/**
 * Whether the site's backend has the procedure (e.g. "minside.updateProfile"). The Danish backend
 * only has the procedures restLink maps; use this to hide features that would always fail there.
 */
export const isProcedureAvailable = (mainLocale: MainLocale, procedure: string): boolean =>
  apiFlavorFor(mainLocale) === "trpc" ||
  Object.prototype.hasOwnProperty.call(restRoutes, procedure);

const apiUrl = () => process.env.NEXT_PUBLIC_EFFEKT_API || "http://localhost:5050";

export const createWebTrpcClient = ({
  flavor,
  getToken,
  url = apiUrl(),
}: {
  flavor: ApiFlavor;
  /** Returns the Auth0 access token (getAccessToken from hooks/useApi) */
  getToken: () => Promise<string>;
  url?: string;
}): TrpcClient =>
  createTRPCClient<AppRouter>({
    links: [
      flavor === "rest"
        ? restLink<AppRouter>({ url, getToken })
        : httpBatchLink({
            url: `${url}/trpc`,
            // Long batches (e.g. distributions for many KIDs) are split into several GETs
            maxURLLength: 2083,
            headers: async () => ({ Authorization: `Bearer ${await getToken()}` }),
          }),
    ],
  });

/**
 * A client for the public donation widget: no token, and no batching (the backend refuses widget
 * mutations inside a batch so the per-IP rate limit counts each one). On the Danish site it is the
 * REST link, which only maps the widget's public reads.
 */
export const createPublicTrpcClient = ({
  flavor,
  url = apiUrl(),
}: {
  flavor: ApiFlavor;
  url?: string;
}): TrpcClient =>
  createTRPCClient<AppRouter>({
    links: [flavor === "rest" ? restLink<AppRouter>({ url }) : httpLink({ url: `${url}/trpc` })],
  });

const publicClients: Partial<Record<ApiFlavor, TrpcClient>> = {};

/**
 * The public client (no token, no batching) for a site, by its Sanity main_locale. Shared per
 * flavor, so server-side rendering and client components reuse one client.
 */
export const publicTrpcFor = (mainLocale: MainLocale | string | null | undefined): TrpcClient => {
  const flavor = apiFlavorFor((mainLocale ?? "no") as MainLocale);
  return (publicClients[flavor] ??= createPublicTrpcClient({ flavor }));
};

/**
 * Where the intervention widget gets its cost per output (site.impactCosts): the Norwegian API on
 * every site, since the impact figures live only in its database (docs/impact-api-migration.md).
 * The Norwegian site uses its own API (so local and e2e builds use the local one); the Danish and
 * Swedish sites, whose NEXT_PUBLIC_EFFEKT_API is their own backend, use NEXT_PUBLIC_IMPACT_API or
 * the production Norwegian API. site.impactCosts answers any origin and is never batched.
 */
export const impactApiUrl = (mainLocale: MainLocale | string | null | undefined): string =>
  (mainLocale ?? "no") === "no"
    ? apiUrl()
    : process.env.NEXT_PUBLIC_IMPACT_API || "https://data.gieffektivt.no";

let impactClient: { url: string; client: TrpcClient } | undefined;

/** The client for site.impactCosts (no token, no batching), for a site by its main_locale */
export const impactTrpcFor = (mainLocale: MainLocale | string | null | undefined): TrpcClient => {
  const url = impactApiUrl(mainLocale);
  if (impactClient?.url !== url)
    impactClient = { url, client: createPublicTrpcClient({ flavor: "trpc", url }) };
  return impactClient.client;
};
